import {
  useFetchOwnPublicProfile,
  useFetchPublicProfile,
} from "@core/features/publicProfile";
import { usePublicProfileSheet, usePlatformStore } from "@platform/store";
import { PublicProfileSheet } from "./PublicProfileSheet";

export type Props = {
  /** Program ID to scope the profile to */
  programId: number | undefined;
};

/**
 * Container component for PublicProfileSheet.
 * Handles data fetching and Zustand state for the public profile preview.
 * When selectedUserId is set, fetches that user's profile; otherwise fetches own profile.
 *
 * @example
 * ```tsx
 * // In ProfilePage.tsx
 * <PublicProfileSheetContainer programId={programId} />
 * ```
 */
export function PublicProfileSheetContainer({ programId }: Props) {
  const user = usePlatformStore((s) => s.user);
  const { isOpen, selectedUserId, close } = usePublicProfileSheet();

  // Fetch own profile when no selectedUserId
  const {
    data: ownProfile,
    isLoading: isOwnLoading,
    error: ownError,
  } = useFetchOwnPublicProfile({
    programId,
    user,
    isEnabled: isOpen && !selectedUserId,
  });

  // Fetch another user's profile when selectedUserId is set
  const {
    data: otherProfile,
    isLoading: isOtherLoading,
    error: otherError,
  } = useFetchPublicProfile({
    userId: selectedUserId,
    programId,
    isEnabled: isOpen && !!selectedUserId,
  });

  const profile = selectedUserId ? otherProfile : ownProfile;
  const isLoading = selectedUserId ? isOtherLoading : isOwnLoading;
  const error = selectedUserId ? otherError : ownError;

  return (
    <PublicProfileSheet
      isOpen={isOpen}
      onClose={close}
      profile={profile ?? null}
      isLoading={isLoading}
      error={error?.message}
    />
  );
}

export default PublicProfileSheetContainer;
